import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Building a small CMS with NextJS, pt2 – CodeWithSwiz",
  "description": "You're the computer, the CMS is your script. Starting small, trying NextJS, comparing microbundle vs. parcel for extracting libraries out of projects.",
  "published": "2020-09-03T00:00:00.000Z",
  "hero": "./img/screenshot-1599145852676.png",
  "categories": "CodeWithSwiz, Livecoding, Technical"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "dPGZBQmVNUg",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Continuing our NextJS app `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/your-first-nextjs-app-codewithswiz/"
      }}>{`where we left off on Sunday`}</a>{`, in this episode we made good progress:`}</p>
    <ul>
      <li parentName="ul">{`added title slugification`}</li>
      <li parentName="ul">{`copypasta instructions to create an article`}</li>
      <li parentName="ul">{`extracted social card API into a custom hook`}</li>
      <li parentName="ul">{`added hero to frontmatter`}</li>
      <li parentName="ul">{`aaaalmost extracted my custom Markdown machinery out of `}<a parentName="li" {...{
          "href": "https://techletter.app"
        }}>{`techletter.app`}</a></li>
    </ul>
    <p>{`You can see it live at `}<a parentName="p" {...{
        "href": "https://swiz-cms.vercel.app"
      }}>{`swiz-cms.vercel.app`}</a>{`. I used it to publish this today 🤘`}</p>
    <h2 {...{
      "id": "slugs-and-article-creation-"
    }}>{`Slugs and article creation 🍝`}</h2>
    <p>{`This is a baby CMS and relies on humans to do the work. Means `}<del parentName="p">{`you`}</del>{` I can start using this before it's finished and start saving time and effort.`}</p>
    <p>{`You're the computer, the CMS is your script.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import slugify from "slugify"

export const CLICopyPasta = ({ title }) => {
  // creates the slug, hooray libraries
  const slug = slugify(title).toLowerCase()

  return (
    <pre>{\`
cd ~/Documents/websites/swizec.com
mkdir src/pages/blog/\${slug}
mkdir src/pages/blog/\${slug}/img
touch src/pages/blog/\${slug}/index.mdx
            \`}</pre>
  )
}
`}</code></pre>
    <p>{`Yep, simple component. Open source `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/slugify"
      }}>{`slugify`}</a>{` makes our slug and we write the instructions. Once is better than always.`}</p>
    <p><a parentName="p" {...{
        "href": "https://i.imgur.com/COaFkQX.png"
      }}>{`Copypasta instructions for article creation`}</a></p>
    <p>{`You can find someone on Fiverrr or Upwork to do these steps for you. Costs $10/week compared to Contentful which is $400/mo.`}</p>
    <p>{`🤔`}</p>
    <h2 {...{
      "id": "custom-hook-for-social-cards"
    }}>{`Custom hook for social cards`}</h2>
    <p>{`A frustration I wanted to solve was adding social cards as hero images to article frontmatter. Fiddly manual process. Error prone as heck.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "872px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/9c368181f7d5a0c6f0efa844a089b98a/65654/Hero-link-shows-up-in-frontmatter002iia.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.97757847533632%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'139\\'%20viewBox=\\'0%200%20400%20139\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%2070v69h401V0H0v70m222-55c0%204%200%205%202%205l1-3c0-3%200-4%202-4l1%204c0%202%200%203%202%203l1-4c0-4%202-4%203%201%200%205%203%204%203-1s-1-6-9-6h-6v5\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9c368181f7d5a0c6f0efa844a089b98a/ca0a1/Hero-link-shows-up-in-frontmatter002iia.webp 223w", "/static/9c368181f7d5a0c6f0efa844a089b98a/75680/Hero-link-shows-up-in-frontmatter002iia.webp 445w", "/static/9c368181f7d5a0c6f0efa844a089b98a/a8a2c/Hero-link-shows-up-in-frontmatter002iia.webp 872w"],
                "sizes": "(max-width: 872px) 100vw, 872px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/9c368181f7d5a0c6f0efa844a089b98a/e92b6/Hero-link-shows-up-in-frontmatter002iia.png 223w", "/static/9c368181f7d5a0c6f0efa844a089b98a/e66bf/Hero-link-shows-up-in-frontmatter002iia.png 445w", "/static/9c368181f7d5a0c6f0efa844a089b98a/65654/Hero-link-shows-up-in-frontmatter002iia.png 872w"],
                "sizes": "(max-width: 872px) 100vw, 872px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9c368181f7d5a0c6f0efa844a089b98a/65654/Hero-link-shows-up-in-frontmatter002iia.png",
                "alt": "Hero link shows up in frontmatter",
                "title": "Hero link shows up in frontmatter",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Hero link shows up in frontmatter`}</figcaption>{`
  `}</figure></p>
    <p>{`There's 2 ways you can do this:`}</p>
    <ol>
      <li parentName="ol">{`Hoist social card fetching out of `}<inlineCode parentName="li">{`<SocialCardImage>`}</inlineCode>{` to top component and push state down through props`}</li>
      <li parentName="ol">{`Create a reusable hook that internally avoids double fetching`}</li>
    </ol>
    <p>{`Following my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/wormhole-state-management/"
      }}>{`Wormhole state management`}</a>{` principle, reusable hook is the way to go.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// components/SocialCardImage
export function useSocialCardQuery(title) {
  return useQuery(["social-card", title], fetchSocialCard)
}
`}</code></pre>
    <p>{`Doesn't look like much but therein lies magic. 🧙‍♂️`}</p>
    <p><a parentName="p" {...{
        "href": "https://github.com/tannerlinsley/react-query"
      }}>{`React Query`}</a>{` dedupes API calls with the same name. Our hook ensures we use the same name and fetch function everywhere.`}</p>
    <p>{`You can now pepper `}<inlineCode parentName="p">{`useSocialCardQuery`}</inlineCode>{` anywhere in your app. It's always going to have the latest state `}<em parentName="p">{`and`}</em>{` avoid refetching, if it knows the result.`}</p>
    <h2 {...{
      "id": "turn-an-old-project-into-a-library"
    }}>{`Turn an old project into a library`}</h2>
    <p>{`This part proved tricky. We tried to extract the markdown machinery from `}<a parentName="p" {...{
        "href": "https://techletter.app"
      }}>{`techletter.app`}</a>{` into a shared library.`}</p>
    <p><img parentName="p" {...{
        "src": "/d4ef2c72959a85066e4b006791939af3/techletterapp-machinery-in-actiond6cc1i.gif",
        "alt": "techletter.app machinery in action"
      }}></img></p>
    <p>{`Extraction itself wasn't that hard:`}</p>
    <ul>
      <li parentName="ul">{`create new project`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`yarn init`}</inlineCode>{` to create `}<inlineCode parentName="li">{`package.json`}</inlineCode></li>
      <li parentName="ul">{`add `}<inlineCode parentName="li">{`microbundle`}</inlineCode>{` for packaging`}</li>
      <li parentName="ul">{`copy files from old project`}</li>
      <li parentName="ul">{`fix relative paths in imports`}</li>
      <li parentName="ul">{`add `}<inlineCode parentName="li">{`index.js`}</inlineCode>{` file that re-exports the public interface`}</li>
    </ul>
    <p>{`Run `}<a parentName="p" {...{
        "href": "https://github.com/developit/microbundle"
      }}><inlineCode parentName="a">{`microbundle`}</inlineCode></a>{` and see it dies on image imports. It's meant for pure TypeScript and JavaScript projects.`}</p>
    <p>{`We use images for loading gifs ...`}</p>
    <p>{`Switch to `}<a parentName="p" {...{
        "href": "https://github.com/parcel-bundler/parcel"
      }}><inlineCode parentName="a">{`parcel`}</inlineCode></a>{` and packaging started working. A matter of changing config in `}<inlineCode parentName="p">{`package.json`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"source": "src/index.js",
"scripts": {
    "dev": "microbundle watch",
    "build": "microbundle"
},

👇

"scripts": {
    "dev": "parcel src/index.js",
    "build": "parcel build src/index.js"
},
`}</code></pre>
    <p>{`Yes no-config bundlers are `}<em parentName="p">{`that`}</em>{` easy. Until you get into the weeds and try to be fancy that is.`}</p>
    <p>{`Unfortunately it didn't work. `}<inlineCode parentName="p">{`global undefined`}</inlineCode>{` whatever the heck that means 🤨`}</p>
    <p>{`But I think I've got a solution, `}<a parentName="p" {...{
        "href": "https://youtube.com/swizecteller"
      }}>{`join me on Monday 6:30pm Pacific`}</a>{`.`}</p>
    <h2 {...{
      "id": "favorite-nextjs-feature-so-far"
    }}>{`Favorite NextJS feature so far?`}</h2>
    <p>{`Initial quick setup was great and those error messages are divine. Beautifully designed, safe as heck, and `}<em parentName="p">{`fast`}</em>{`.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/30b9897a4f8e5eb77a7d7855f92a9862/373fb/An-error-message-in-NextJS228g8i.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "55.60538116591929%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'222\\'%20viewBox=\\'0%200%20400%20222\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M0%20111v111h16V12h367v105l1%20105c2%200%202-13%202-105l1-105v210h14V0H0v111m23-74l-1%203c0%203%200%203%206%203%204%200%205%200%205-2h1c2%202%2011%203%2011%201l1-2c0-2-8-2-9%200l-1-1-5-1h-5l-3-1m15%2063l1%203h1c0-2%200-2%201-1h11l2%201h8l6-1h1l9%201%207-1v-1h2c0-2-10-2-12%200-1%201-2%202-1%200l-3-1-4%201h-1l-4-1h-2l-2-1-2%201H45c-1-1-2-1-2%201h-1c0-2-2-3-2-1h-2m75%200c-3%200-4%201-4%202h5l4%201%204-1h19c1%202%205%203%205%201h1l1-1h3l3%201%202-1h51l1-1%201%201%202%201%203-1h8l3%201%202-1h-1l-1-2h-11a392%20392%200%2001-54%200h-14l-4%201-4-1h-25m-49%2015h-1l-9-1c-8%200-8%200-8%203%200%202%200%202%201%201l4-1%203-1h2c1%202%206%202%206%201h10l2%201h2l4-1%205-1c0-1%201-1%202%201%201%201%203%200%203-2s-7-1-7%200h-1l-6-1h-7c0-2-3-1-3%201h-1l-1-2v2m67%2012c-1%201%200%202%201%201l1%201h7l2%201h3c2-2%205-2%207-1%201%201%204%200%204-1h1l7%201%209-1a99%2099%200%2000-28-1h-14m-91%2026l-1%203h9l4-1h1l2%202%201-1h13a7%207%200%20007%200h18c1%201%202%200%203-1h2l2%201h5l1-1h7c1%201%203%202%205%201h17l4-2v-1h-6l-2%201h-5l-6-1h-14l-2%201H78c1-2-2-3-3%200l-1%201-1-1-6-1h-7l-2-1-2%202H44l-1%201-1-1-1-2-1%201m147%200h-5l-10%202%205%201h14c2%201%206%200%207-1h1l2%201h3l2-1h2c0%201%204%202%206%200%201-1%201-1%201%201h7l3%201%202-1h1l2-2v-1h-4l-2%201h-3l-1-1-2-1-2%201h-10l-12%201h-4l-3-1M38%20180l1%203h1c0-2%200-2%201-1h11l2%201h2l2-2v1l4%201%206-1h15c0-3-6-3-7-2-2%202-4%203-3%201l-3-1-3%201h-1l-4-1h-2l-2-1-2%202h-1l-5-1h-5l-2%201h-1c0-2-2-3-2-1h-2m98%200h-4c-2%200-3%200-2%201v1c-1%201%201%201%204%201l6-1h1c1%202%205%203%205%201h1l1-1h3l3%201%202-1h51l1-1%201%201%202%201%203-1c0-1%201-1%201%201h7l5-1h-1l-1-2h-26a129%20129%200%2001-12%200h-41l-4%201-4-1h-2\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/30b9897a4f8e5eb77a7d7855f92a9862/ca0a1/An-error-message-in-NextJS228g8i.webp 223w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/75680/An-error-message-in-NextJS228g8i.webp 445w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/8d1ba/An-error-message-in-NextJS228g8i.webp 890w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/3838e/An-error-message-in-NextJS228g8i.webp 1335w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/e11e5/An-error-message-in-NextJS228g8i.webp 1780w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/8fc0f/An-error-message-in-NextJS228g8i.webp 2068w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/30b9897a4f8e5eb77a7d7855f92a9862/e92b6/An-error-message-in-NextJS228g8i.png 223w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/e66bf/An-error-message-in-NextJS228g8i.png 445w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/4ef49/An-error-message-in-NextJS228g8i.png 890w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/4e814/An-error-message-in-NextJS228g8i.png 1335w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/701e9/An-error-message-in-NextJS228g8i.png 1780w", "/static/30b9897a4f8e5eb77a7d7855f92a9862/373fb/An-error-message-in-NextJS228g8i.png 2068w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/30b9897a4f8e5eb77a7d7855f92a9862/4ef49/An-error-message-in-NextJS228g8i.png",
                "alt": "An error message in NextJS",
                "title": "An error message in NextJS",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`An error message in NextJS`}</figcaption>{`
  `}</figure></p>
    <p>{`With client-side bugs you get a blurred-out view of your app behind the error. Useful, no, cool as heck, yeah!`}</p>
    <p>{`And your browser almost always reconnects to the development server even after a restart.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/QBKagmzOgZnqadpx3B/giphy-loop.mp4?cid=7cf28b0f4mnghemkfxc2jj6a2qvzrf0env63wcm7xex14oz1&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      